import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { AppState, Table, UserRole, TableAction, ActionContext, TableStatus } from '../types';
import { createInitialAppState, applyAction } from '../utils/tableUtils';
import { apiService } from '../services/apiService';
import { signalRService } from '../services/signalRService';
import { audioService } from '../services/audioService';

// Context Types
interface AppContextType {
  state: AppState;
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  executeAction: (tableId: number, action: TableAction) => void;
  updateTableCount: (count: number) => void;
  updateTableNumber: (tableId: number, number: string) => void;
  resetAllTables: () => void;
  syncFromStorage: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  signalRState: string;
  signalRConnected: boolean;
}

// Actions für Reducer
type AppActionType =
  | { type: 'SET_STATE'; payload: AppState }
  | { type: 'EXECUTE_TABLE_ACTION'; payload: ActionContext }
  | { type: 'UPDATE_TABLE_COUNT'; payload: number }
  | { type: 'UPDATE_TABLE_NUMBER'; payload: { tableId: number; number: string } }
  | { type: 'RESET_ALL_TABLES' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

// Reducer
const appReducer = (state: AppState, action: AppActionType): AppState => {
  switch (action.type) {
    case 'SET_STATE':
      // Migration: Sicherstellen dass alle Tische actionTimestamps haben
      const migratedPayload = {
        ...action.payload,
        tables: action.payload.tables.map(table => ({
          ...table,
          actionTimestamps: table.actionTimestamps || {} // Default zu leerem Objekt falls nicht vorhanden
        }))
      };
      return migratedPayload;
      
    case 'EXECUTE_TABLE_ACTION':
      const { tableId } = action.payload;
      const tableIndex = state.tables.findIndex(t => t.id === tableId);
      if (tableIndex === -1) return state;
      
      const updatedTable = applyAction(state.tables[tableIndex], action.payload);
      const newTables = [...state.tables];
      newTables[tableIndex] = updatedTable;
      
      return {
        ...state,
        tables: newTables,
        lastSync: new Date().toISOString(),
        version: state.version + 1
      };
      
    case 'UPDATE_TABLE_COUNT':
      const newCount = action.payload;
      const currentTables = state.tables;
      let updatedTables: Table[];
      
      if (newCount > currentTables.length) {
        // Füge neue Tische hinzu
        const newTables = Array.from(
          { length: newCount - currentTables.length },
          (_, i) => ({
            id: currentTables.length + i + 1,
            number: `#${currentTables.length + i + 1}`,
            status: TableStatus.AVAILABLE,
            actions: {
            kitchenStarted: false,
            pizzaConfirmed: false,
            kitchenReady: false,
            pizzaReady: false,
          },
          actionTimestamps: {}, // Leer initialisieren
            lastUpdated: new Date().toISOString(),
            updatedBy: UserRole.KITCHEN
          })
        );
        updatedTables = [...currentTables, ...newTables];
      } else {
        // Entferne Tische (nur wenn sie available sind)
        updatedTables = currentTables
          .filter((table, index) => 
            index < newCount || table.status !== TableStatus.AVAILABLE
          )
          .slice(0, newCount);
      }
      
      return {
        ...state,
        tables: updatedTables,
        totalTables: newCount,
        lastSync: new Date().toISOString(),
        version: state.version + 1
      };
      
    case 'UPDATE_TABLE_NUMBER':
      const { tableId: id, number } = action.payload;
      const tableIdx = state.tables.findIndex(t => t.id === id);
      if (tableIdx === -1) return state;
      
      const tables = [...state.tables];
      tables[tableIdx] = { ...tables[tableIdx], number };
      
      return {
        ...state,
        tables,
        lastSync: new Date().toISOString(),
        version: state.version + 1
      };
      
    case 'RESET_ALL_TABLES':
      // Setze alle Tische auf verfügbar zurück und resette die Version auf 1
      const resetTables = state.tables.map(table => ({
        ...table,
        status: TableStatus.AVAILABLE,
        actions: {
          kitchenStarted: false,
          pizzaConfirmed: false,
          kitchenReady: false,
          pizzaReady: false,
        },
        actionTimestamps: {}, // Leer initialisieren
        lastUpdated: new Date().toISOString(),
        updatedBy: UserRole.KITCHEN
      }));
      
      return {
        ...state,
        tables: resetTables,
        lastSync: new Date().toISOString(),
        version: 1  // Version auf 1 zurücksetzen
      };
      
    default:
      return state;
  }
};

// Context erstellen
const AppContext = createContext<AppContextType | undefined>(undefined);

// Context Provider
interface AppProviderProps {
  children: React.ReactNode;
  initialTableCount?: number;
}

export const AppProvider: React.FC<AppProviderProps> = ({ 
  children, 
  initialTableCount = 20 
}) => {
  const [state, dispatch] = useReducer(appReducer, createInitialAppState(initialTableCount));
  const [userRole, setUserRole] = React.useState<UserRole>(UserRole.KITCHEN);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [signalRState, setSignalRState] = React.useState<string>('Connecting');
  const [signalRConnected, setSignalRConnected] = React.useState(false);
  const [signalRInitialized, setSignalRInitialized] = React.useState(false);
  
  // Ref zum Tracken des vorherigen State für Sound-Benachrichtigungen
  const previousStateRef = useRef<AppState | null>(null);

  // URL-basierte Rollenerkennung
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roleParam = urlParams.get('role');
    
    if (roleParam === 'kitchen') {
      setUserRole(UserRole.KITCHEN);
    } else if (roleParam === 'pizza') {
      setUserRole(UserRole.PIZZA);
    }
  }, []);

  const executeAction = useCallback((tableId: number, action: TableAction) => {
    const context: ActionContext = {
      tableId,
      action,
      role: userRole,
      timestamp: new Date().toISOString()
    };
    
    dispatch({ type: 'EXECUTE_TABLE_ACTION', payload: context });
  }, [userRole]);

  const updateTableCount = useCallback((count: number) => {
    dispatch({ type: 'UPDATE_TABLE_COUNT', payload: count });
  }, []);

  const updateTableNumber = useCallback((tableId: number, number: string) => {
    dispatch({ type: 'UPDATE_TABLE_NUMBER', payload: { tableId, number } });
  }, []);

  const resetAllTables = useCallback(() => {
    setIsInitialLoad(false); // Stelle sicher, dass das Reset gespeichert wird
    dispatch({ type: 'RESET_ALL_TABLES' });
  }, []);

  // Azure Storage Sync - VERPFLICHTEND, kein Fallback
  const syncFromStorage = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Prüfe ob API verfügbar ist
      if (!(await apiService.isAvailable())) {
        throw new Error('API ist nicht verfügbar. Bitte stellen Sie sicher, dass der Backend-Service läuft.');
      }
      
      const loadedState = await apiService.loadAppState();
      
      // Wichtig: Übernehme JEDEN unterschiedlichen State, nicht nur neuere Versionen!
      // Das ermöglicht sowohl Updates (Version hoch) als auch Resets (Version runter)
      if (loadedState && loadedState.version !== state.version) {
        console.log(`🔄 State aus Azure Storage übernommen: Lokal=${state.version} → Azure=${loadedState.version}`);
        setIsInitialLoad(false); // Stelle sicher, dass nachfolgende Änderungen gespeichert werden
        dispatch({ type: 'SET_STATE', payload: loadedState });
      } else if (loadedState) {
        console.log('✅ State ist bereits aktuell, Version:', loadedState.version);
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Azure Storage Sync fehlgeschlagen';
      setError(errorMessage);
      console.error('Azure Storage Sync Fehler:', err);
    } finally {
      setIsLoading(false);
    }
  }, [state.version]);

  // Auto-save zu API bei Änderungen - VERPFLICHTEND
  const saveToStorage = useCallback(async (appState: AppState) => {
    try {
      // Prüfe ob API verfügbar ist
      if (!(await apiService.isAvailable())) {
        throw new Error('API ist nicht verfügbar. Kann State nicht speichern.');
      }
      
      await apiService.saveAppState(appState);
      console.log('State erfolgreich via API gespeichert, Version:', appState.version);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Speichern via API fehlgeschlagen';
      console.error('Fehler beim Speichern:', err);
      setError(errorMessage);
    }
  }, []);

  // Save automatisch bei State-Änderungen
  useEffect(() => {
    // Speichere nur wenn es nicht der initiale Load ist
    if (!isInitialLoad) {
      saveToStorage(state);
    }
  }, [state, saveToStorage, isInitialLoad]);

  // Sound-Benachrichtigung bei Status-Änderungen (nicht durch eigene Rolle)
  useEffect(() => {
    // Skip beim initialen Load und wenn wir noch keinen vorherigen State haben
    if (isInitialLoad || !previousStateRef.current) {
      previousStateRef.current = state;
      return;
    }

    const previousState = previousStateRef.current;

    // Prüfe jeden Tisch auf Status-Änderungen
    state.tables.forEach((currentTable) => {
      const previousTable = previousState.tables.find(t => t.id === currentTable.id);
      
      if (!previousTable) return;

      // Prüfe ob Status sich geändert hat
      const statusChanged = previousTable.status !== currentTable.status;
      const changedByOtherRole = currentTable.updatedBy !== userRole;

      if (!statusChanged || !changedByOtherRole) return;

      const newStatus = currentTable.status;
      const previousStatus = previousTable.status;

      // DREI-TON für PIZZAIOLO:
      // - Neue Pizza angefordert: AVAILABLE → COOKING
      // - Storno angefordert: beliebiger Status → CANCELLED
      if (userRole === UserRole.PIZZA) {
        if (
          (previousStatus === TableStatus.AVAILABLE && newStatus === TableStatus.COOKING) ||
          newStatus === TableStatus.CANCELLED
        ) {
          console.log(`🔔 3-Ton: Tisch ${currentTable.number} - ${previousStatus} → ${newStatus} (Pizzaiolo benachrichtigt)`);
          audioService.playNotification();
        }
      }

      // DREI-TON für KÜCHE:
      // - Storno angefordert: beliebiger Status → CANCELLED
      if (userRole === UserRole.KITCHEN) {
        if (newStatus === TableStatus.CANCELLED) {
          console.log(`🔔 3-Ton: Tisch ${currentTable.number} - ${previousStatus} → ${newStatus} (Küche benachrichtigt)`);
          audioService.playNotification();
        }
      }

      // ZWEI-TON für beide Rollen wenn Gegenpartei fertig ist:
      // - KITCHEN_READY_PENDING: Küche ist fertig → Pizzaiolo hört 2-Ton
      // - PARTIALLY_READY: Pizza ist fertig → Küche hört 2-Ton (oder auch Pizza wenn Küche fertig)
      // - READY: Beide sind fertig → beide hören 2-Ton
      if (
        newStatus === TableStatus.KITCHEN_READY_PENDING ||
        newStatus === TableStatus.PARTIALLY_READY ||
        newStatus === TableStatus.READY
      ) {
        console.log(`🔔 2-Ton: Tisch ${currentTable.number} - ${previousStatus} → ${newStatus} (Fertig-Meldung)`);
        audioService.playReadyNotification();
      }
    });

    // Aktualisiere den Ref für den nächsten Vergleich
    previousStateRef.current = state;
  }, [state, userRole, isInitialLoad]);

  // Initial Load von Azure Storage - VERPFLICHTEND
  useEffect(() => {
    const loadInitialState = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Prüfe API Verfügbarkeit
        if (!(await apiService.isAvailable())) {
          throw new Error('API ist nicht verfügbar. Bitte stellen Sie sicher, dass der Backend-Service läuft.');
        }

        const loadedState = await apiService.loadAppState();
        
        if (loadedState) {
          dispatch({ type: 'SET_STATE', payload: loadedState });
          console.log('✅ Bestehender State aus API geladen, Version:', loadedState.version);
        } else {
          // Nur wenn wirklich keine Daten existieren - erstelle einmalig einen initialen State
          console.log('⚠️ Keine Daten gefunden - erstelle EINMALIG initialen State');
          const initialState = createInitialAppState(20);
          
          // Speichere initialen State via API (nur einmal!)
          await apiService.saveAppState(initialState);
          console.log('✅ Initialer State erstellt und gespeichert - Version:', initialState.version);
          
          // Setze State in der App
          dispatch({ type: 'SET_STATE', payload: initialState });
        }
        
        // Nach dem initialen Load setzen wir isInitialLoad auf false
        setIsInitialLoad(false);
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Laden des initialen States fehlgeschlagen';
        console.error('Fehler beim Laden des initialen States:', err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialState();
  }, []); // Nur einmal beim Mount ausführen

  // SignalR Setup - Ersetzt das 3-Sekunden-Polling
  useEffect(() => {
    // Verhindere mehrfache Initialisierung (React Strict Mode)
    if (signalRInitialized) return;

    // SignalR Event Handlers
    signalRService.onTableUpdate((updatedState: AppState) => {
      console.log('📡 SignalR: Real-time update empfangen:', updatedState.version);
      // Nur updaten wenn Version unterschiedlich ist
      if (updatedState.version !== state.version) {
        dispatch({ type: 'SET_STATE', payload: updatedState });
        console.log(`🔄 Real-time State Update: ${state.version} → ${updatedState.version}`);
      }
    });

    signalRService.onConnectionStateChanged((connectionState) => {
      console.log('🔌 SignalR Connection State:', connectionState);
      setSignalRState(connectionState);
      setSignalRConnected(connectionState === 'Connected');
      
      // Clear error wenn SignalR unavailable ist (das ist normal in Development)
      if (connectionState === 'Unavailable') {
        setError(null);
        console.log('ℹ️ SignalR nicht verfügbar - App nutzt API-Polling als Fallback');
      } else if (connectionState === 'Connected') {
        console.log('✅ SignalR verbunden - Real-time Updates aktiv, Polling gestoppt');
      }
    });

    signalRService.onError((error: string) => {
      // Nur echte Errors loggen, nicht "Service Unavailable"
      if (!error.includes('503') && !error.includes('Service Unavailable')) {
        console.error('❌ SignalR Error:', error);
        setError(`SignalR: ${error}`);
      } else {
        console.log('ℹ️ SignalR:', error);
      }
    });

    // Initialisiere SignalR Connection (nur einmal)
    const initializeSignalR = async () => {
      try {
        setSignalRInitialized(true);
        await signalRService.initialize();
      } catch (error) {
        console.error('❌ SignalR Initialization Error:', error);
        // Reset nur bei echten Fehlern
        setSignalRInitialized(false);
      }
    };

    initializeSignalR();

    // Cleanup beim Unmount
    return () => {
      signalRService.disconnect();
      setSignalRInitialized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur einmal beim Mount - entferne signalRInitialized aus Dependencies!

  // Fallback-Polling nur wenn SignalR definitiv nicht verfügbar ist
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    // Nur Polling starten wenn SignalR definitiv nicht verfügbar ist
    const shouldUseFallbackPolling = !signalRConnected && 
      (signalRState === 'Unavailable' || signalRState === 'Disconnected');
    
    if (shouldUseFallbackPolling) {
      console.log(`🔄 SignalR ${signalRState} - starte Fallback-Polling (5s)`);
      interval = setInterval(async () => {
        try {
          const updateCheck = await apiService.checkForUpdates(state.version);
          if (updateCheck.hasUpdates) {
            console.log('📦 Polling: Updates detected, syncing...');
            syncFromStorage();
          }
        } catch (err) {
          console.warn('📦 Polling: Update check failed:', err);
        }
      }, 5000);
    } else if (signalRConnected) {
      console.log('📡 SignalR connected - Polling gestoppt (Real-time aktiv)');
    } else if (signalRState === 'Connecting') {
      console.log('🔌 SignalR verbindet sich - warte auf Ergebnis...');
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [syncFromStorage, state.version, signalRConnected, signalRState]);

  const contextValue: AppContextType = {
    state,
    userRole,
    setUserRole,
    executeAction,
    updateTableCount,
    updateTableNumber,
    resetAllTables,
    syncFromStorage,
    isLoading,
    error,
    signalRState,
    signalRConnected
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook für Context-Verwendung
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};